import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { adminSettingsApi, type AdminSettingItem, type AdminSettingMeta, type AdminSettingMetaExtended } from '../api/client';

/** F-1 (2026-06-09 DCS): transcript_retention_mode のキー名と既定値 */
const TRANSCRIPT_RETENTION_KEY = 'transcript_retention_mode';
const TRANSCRIPT_RETENTION_DEFAULT = 'retain_6months';

/** レスポンスに含まれるキーを「設定済み」としてマーク（値は保持しない） */
function toItemsMap(items: AdminSettingItem[]): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  items.forEach((item) => {
    if (item.key) map[item.key] = true;
  });
  return map;
}

/** 管理者向け 組織別設定（APIキー登録・更新） */
export default function AdminSettings() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  /** 取得した設定で「キーが存在する」か（値は保持せず、設定済み表示のみに利用） */
  const [keyExists, setKeyExists] = useState<Record<string, boolean>>({});
  const [openai_api_key, setOpenaiApiKey] = useState('');
  const [google_api_key, setGoogleApiKey] = useState('');
  const [google_application_credentials, setGoogleApplicationCredentials] = useState('');
  /** GET /v1/admin/settings/meta から取得した各キーのメタ情報。失敗時は空 */
  const [metaMap, setMetaMap] = useState<Record<string, AdminSettingMeta>>({});
  /** F-1: transcript_retention_mode の現在値とフラグ */
  const [retentionMode, setRetentionMode] = useState<string>(TRANSCRIPT_RETENTION_DEFAULT);
  const [retentionIsSet, setRetentionIsSet] = useState<boolean>(false);
  /** F-1: 読み込み直後の値。submit 時に変更検出して dirty なら送信 */
  const [retentionInitial, setRetentionInitial] = useState<string>(TRANSCRIPT_RETENTION_DEFAULT);
  /** F-1: GET レスポンス item に同梱された meta（options を含む拡張版） */
  const [retentionMeta, setRetentionMeta] = useState<AdminSettingMetaExtended | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    // settings と meta を並列取得。meta は失敗してもフォールバック動作させる
    Promise.all([
      adminSettingsApi.get(token),
      adminSettingsApi.getMeta(token).catch(() => null),
    ])
      .then(([res, metaRes]) => {
        const items = res.items ?? [];
        setKeyExists(toItemsMap(items));
        // F-1: transcript_retention_mode の値と meta を抽出
        const retentionItem = items.find((it) => it.key === TRANSCRIPT_RETENTION_KEY);
        if (retentionItem) {
          const value = typeof retentionItem.value === 'string' && retentionItem.value
            ? retentionItem.value
            : TRANSCRIPT_RETENTION_DEFAULT;
          setRetentionMode(value);
          setRetentionInitial(value);
          setRetentionIsSet(retentionItem.is_set === true);
          setRetentionMeta(retentionItem.meta ?? null);
        } else {
          setRetentionMode(TRANSCRIPT_RETENTION_DEFAULT);
          setRetentionInitial(TRANSCRIPT_RETENTION_DEFAULT);
          setRetentionIsSet(false);
          setRetentionMeta(null);
        }
        if (metaRes && Array.isArray(metaRes.items)) {
          const m: Record<string, AdminSettingMeta> = {};
          metaRes.items.forEach((it) => {
            if (it.key && it.meta) m[it.key] = it.meta;
          });
          setMetaMap(m);
        } else {
          setMetaMap({});
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('admin.settingsErrorLoad'));
        setKeyExists({});
        setMetaMap({});
        setRetentionMode(TRANSCRIPT_RETENTION_DEFAULT);
        setRetentionInitial(TRANSCRIPT_RETENTION_DEFAULT);
        setRetentionIsSet(false);
        setRetentionMeta(null);
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    setSaveError(null);
    setSavedMessage(null);
    const items: Array<{ key: string; value: string; encrypted?: boolean }> = [];
    if (openai_api_key.trim()) {
      items.push({ key: 'openai_api_key', value: openai_api_key.trim(), encrypted: false });
    }
    if (google_api_key.trim()) {
      items.push({ key: 'google_api_key', value: google_api_key.trim(), encrypted: false });
    }
    if (google_application_credentials.trim()) {
      items.push({ key: 'google_application_credentials', value: google_application_credentials.trim(), encrypted: false });
    }
    // F-1: transcript_retention_mode は変更時のみ送信
    if (retentionMode !== retentionInitial) {
      items.push({ key: TRANSCRIPT_RETENTION_KEY, value: retentionMode, encrypted: false });
    }
    if (items.length === 0) {
      setSaveError(t('admin.settingsFillOne'));
      return;
    }
    setSaving(true);
    adminSettingsApi
      .update({ items }, token)
      .then(() => {
        setSavedMessage(t('admin.settingsSaved'));
        setKeyExists((prev) => {
          const next = { ...prev };
          items.forEach((item) => { next[item.key] = true; });
          return next;
        });
        setOpenaiApiKey('');
        setGoogleApiKey('');
        setGoogleApplicationCredentials('');
        // F-1: retention を保存していたら「設定済み」状態 + initial を更新
        if (retentionMode !== retentionInitial) {
          setRetentionInitial(retentionMode);
          setRetentionIsSet(true);
        }
      })
      .catch((err) => {
        setSaveError(err instanceof Error ? err.message : t('admin.settingsErrorSave'));
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="page admin-settings-page">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="page admin-settings-page">
      <h1>{t('admin.settings')}</h1>
      <p className="admin-settings-intro">{t('admin.settingsIntro')}</p>

      {error && (
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-card admin-settings-form">
        <div className="admin-form-group">
          <label htmlFor="admin-settings-openai">{t('admin.settingsOpenAiKey')}</label>
          <input
            id="admin-settings-openai"
            type="password"
            autoComplete="off"
            className="admin-input-masked"
            value={openai_api_key}
            onChange={(e) => setOpenaiApiKey(e.target.value)}
            placeholder={keyExists['openai_api_key'] ? t('admin.settingsPlaceholderSet') : t('admin.settingsPlaceholderNotSet')}
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="admin-settings-google">{t('admin.settingsGoogleApiKey')}</label>
          <input
            id="admin-settings-google"
            type="password"
            autoComplete="off"
            className="admin-input-masked"
            value={google_api_key}
            onChange={(e) => setGoogleApiKey(e.target.value)}
            placeholder={keyExists['google_api_key'] ? t('admin.settingsPlaceholderSet') : t('admin.settingsPlaceholderNotSet')}
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="admin-settings-google-creds">{t('admin.settingsGoogleAppCreds')}</label>
          <input
            id="admin-settings-google-creds"
            type="password"
            autoComplete="off"
            className="admin-input-masked"
            value={google_application_credentials}
            onChange={(e) => setGoogleApplicationCredentials(e.target.value)}
            placeholder={keyExists['google_application_credentials'] ? t('admin.settingsPlaceholderSet') : t('admin.settingsPlaceholderNotSet')}
          />
        </div>

        {/*
          課題 #101-7（2026-10-07 高尾さん決定）: 今の Alert 通知の判定に使っていない設定 6 つ（閾値・連続日数など）は、
          画面から外す。変えても効かない設定を残すと、管理者が通知の条件を変えたつもりになるため。
          判定のしかた（月次 MCS の判定）は変えないので、通知がどう送られるかだけを説明として残す
        */}
        <section className="admin-settings-fieldset admin-alert-info" data-testid="alert-info">
          <h3>{t('admin.alertSettings.title')}</h3>
          <p className="admin-form-hint">{t('admin.alertSettings.intro')}</p>
          <p className="admin-form-hint">{t('admin.alertSettings.note')}</p>
        </section>

        {/* F-1 (2026-06-09 DCS): プライバシー / データ保持 — transcript_retention_mode 切替 */}
        <fieldset className="admin-settings-fieldset">
          <legend>{t('admin.privacySettings.title')}</legend>
          <p className="admin-form-hint">{t('admin.privacySettings.intro')}</p>

          {(() => {
            // meta は GET レスポンスに同梱されたもの優先、フォールバックは /meta 経由
            const meta = retentionMeta ?? (metaMap[TRANSCRIPT_RETENTION_KEY] as AdminSettingMetaExtended | undefined) ?? null;
            const options = Array.isArray(meta?.options) && meta?.options?.length
              ? meta.options
              : ['retain_6months', 'immediate_discard'];
            return (
              <div className="admin-form-group">
                <p className="admin-form-label-text">{t('admin.privacySettings.retentionLabel')}</p>
                {!retentionIsSet && (
                  <p className="admin-form-hint admin-settings-default-note">
                    {t('admin.privacySettings.defaultApplied')}
                  </p>
                )}
                <div className="admin-radio-group" role="radiogroup" aria-label={t('admin.privacySettings.retentionLabel')}>
                  {options.map((opt) => (
                    <label key={opt} className="admin-radio-label">
                      <input
                        type="radio"
                        name="transcript-retention-mode"
                        value={opt}
                        checked={retentionMode === opt}
                        onChange={() => setRetentionMode(opt)}
                      />
                      <span className="admin-radio-text">
                        <strong>{t(`admin.privacySettings.option.${opt}.label`, { defaultValue: opt })}</strong>
                        <span className="admin-radio-desc">
                          {t(`admin.privacySettings.option.${opt}.desc`, { defaultValue: '' })}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="admin-form-hint admin-settings-info-tip">
                  {t('admin.privacySettings.tipApplyTiming')}
                </p>
                <p className="admin-form-hint">{t('admin.privacySettings.tipApiUnchanged')}</p>
                {meta?.description_ja && (
                  <p className="admin-form-hint admin-settings-meta-desc">{meta.description_ja}</p>
                )}
              </div>
            );
          })()}
        </fieldset>

        {saveError && <p className="admin-form-error">{saveError}</p>}
        {savedMessage && <p className="admin-settings-saved" role="status">{savedMessage}</p>}

        <button type="submit" className="admin-btn-primary" disabled={saving}>
          {saving ? t('common.loading') : t('admin.settingsSave')}
        </button>
      </form>

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.dashboard')}</Link>
      </p>
    </div>
  );
}
